class HubSpotRoutingService {
  static WIDGET_ROUTE = '/widgets/hubspot-inbound-call-extension'
  static LEGACY_WIDGET_ROUTE = '/widgets/hubspot-call-extension'
  static SMS_WIDGET_ROUTE_PREFIX = '/widgets/texting/'

  // True if the path is a HubSpot widget route, matched on exact pathname so a stray substring can't
  static isWidgetExtensionPath (path) {
    if (!path) {
      return false
    }
    const pathname = path.split('?')[0]
    return pathname === this.WIDGET_ROUTE || pathname === this.LEGACY_WIDGET_ROUTE
  }

  // True if the path is the SMS (texting) widget route, so login can send the user back to it.
  static isSmsWidgetPath (path) {
    if (!path) {
      return false
    }
    const pathname = path.split('?')[0]
    return pathname.startsWith(this.SMS_WIDGET_ROUTE_PREFIX)
  }

  /**
   * Checks if the current window is running inside an iframe
   *
   * @returns {boolean} True if running in an iframe
   */
  static isInIframe () {
    if (typeof window === 'undefined') {
      return false
    }
    return window.self !== window.top
  }

  /**
   * Extracts domain from referrer URL.
   *
   * @returns {string|null} Domain hostname or null if referrer unavailable
   */
  static extractHubSpotDomainFromReferrer () {
    if (typeof document === 'undefined' || !document.referrer) {
      return null
    }

    try {
      const referrerUrl = new URL(document.referrer)
      return referrerUrl.hostname.toLowerCase()
    } catch (e) {
      console.warn('[HubSpot Routing] Failed to parse referrer URL:', e)
      return null
    }
  }

  /**
   * Checks if referrer URL contains HubSpot indicators
   *
   * @param {string} hubspotDomain Domain to check for
   * @returns {boolean}
   */
  static hasHubSpotReferrer (hubspotDomain = 'app.hubspot.com') {
    if (typeof document === 'undefined' || !document.referrer) {
      return false
    }

    const referrer = document.referrer.toLowerCase()
    return referrer.includes('hubspot-call-extension') ||
           referrer.includes(hubspotDomain) ||
           referrer.includes('hubspot')
  }

  /**
   * Checks if referrer URL contains 'hubspot' or 'hubspot-call-extension' substring. Used to determine if we should extract domain from referrer
   *
   * @returns {boolean}
   */
  static referrerHasHubSpotIndicators () {
    if (typeof document === 'undefined' || !document.referrer) {
      return false
    }

    const referrer = document.referrer.toLowerCase()
    return referrer.includes('hubspot') || referrer.includes('hubspot-call-extension')
  }

  /**
   * Checks if HubSpot integration is enabled for the company
   *
   * @param {object} company
   * @returns {boolean}
   */
  static isHubSpotIntegrationEnabled (company) {
    return company?.hubspot_integration_enabled === true
  }

  /**
   * Gets HubSpot domain with fallback: company settings → profile settings → referrer extraction → default
   *
   * @param {object} company
   * @param {object} profile
   * @param {boolean} checkReferrer Whether to extract domain from referrer if not in settings
   * @returns {string} HubSpot domain
   */
  static getHubSpotDomain (company = null, profile = null, checkReferrer = true) {
    const domainFromSettings = company?.hubspot_company_ui_domain ||
                                profile?.company?.hubspot_company_ui_domain

    if (domainFromSettings) {
      return domainFromSettings
    }

    if (checkReferrer) {
      const referrerDomain = this.extractHubSpotDomainFromReferrer()
      if (referrerDomain) {
        return referrerDomain
      }
    }

    return 'app.hubspot.com'
  }

  /**
   * Whether a URL is a safe HubSpot redirect target. The post-connect flow
   * hands us a `redirectUrl` query param that is navigated to directly, so it
   * must be validated to prevent open-redirect attacks (CWE-601). HubSpot app
   * UI domains are always https and under hubspot.com (app.hubspot.com,
   * app-euN.hubspot.com, etc.).
   *
   * @param {string} url
   * @returns {boolean}
   */
  static isAllowedHubSpotRedirect (url) {
    try {
      const { hostname, protocol } = new URL(url)

      if (protocol !== 'https:') {
        return false
      }

      return hostname === 'hubspot.com' || hostname.endsWith('.hubspot.com')
    } catch {
      return false
    }
  }

  /**
   * Checks if route is the HubSpot widget route
   *
   * @param {object} route Vue router route object
   * @returns {boolean}
   */
  static isHubSpotWidgetRoute (route) {
    if (!route) {
      return false
    }

    return route.name === 'HubSpot Call Extension' ||
           route.path?.includes('hubspot-call-extension') ||
           route.matched?.some(r => r.meta?.isHubSpotWidget)
  }

  /**
   * Checks if we're in a HubSpot context (for listener setup, etc.)
   * Looks for route, redirect query, widget state, or referrer indicators
   *
   * @param {object} options
   * @param {object} options.route
   * @param {string} options.redirectQuery
   * @param {boolean} options.isHubSpotWidget
   * @returns {boolean}
   */
  static isHubSpotContext ({ route = null, redirectQuery = null, isHubSpotWidget = false }) {
    const hasHubSpotRedirect = redirectQuery && redirectQuery.includes('hubspot-call-extension')
    const hasHubSpotReferrer = this.hasHubSpotReferrer()
    const isHubSpotExtension = this.isHubSpotWidgetRoute(route)
    const isInIframe = this.isInIframe()

    const isHubSpotIframe = isInIframe && (hasHubSpotReferrer || hasHubSpotRedirect || isHubSpotExtension || isHubSpotWidget)

    return hasHubSpotRedirect || hasHubSpotReferrer || isHubSpotWidget || isHubSpotExtension || isHubSpotIframe
  }

  /**
   * Detects HubSpot widget context and determines if redirect is needed
   *
   * @param {object} options
   * @param {object} options.route
   * @param {object} options.company
   * @param {object} options.profile
   * @param {boolean} options.isHubSpotWidget
   * @param {string} options.redirectPath Original redirect path
   * @returns {object} Context detection result with shouldRedirect, hubspotDomain, etc.
   */
  static detectHubSpotContext ({ route = null, company = null, profile = null, isHubSpotWidget = false, redirectPath = null }) {
    const isHubSpotExtension = this.isHubSpotWidgetRoute(route)
    const isInIframe = this.isInIframe()
    const isHubSpotEnabled = this.isHubSpotIntegrationEnabled(company)
    const hasHubSpotRedirect = redirectPath && redirectPath.includes('hubspot-call-extension')
    const referrerHasIndicators = this.referrerHasHubSpotIndicators()

    // Determine if we have initial HubSpot indicators (before domain extraction)
    // This includes route, widget state, redirect path, or referrer with 'hubspot' substring
    const hasInitialHubSpotIndicator = isHubSpotExtension ||
                                       isHubSpotWidget ||
                                       hasHubSpotRedirect ||
                                       referrerHasIndicators

    // Only extract domain from referrer if we have indicators or are in iframe with HubSpot indicators
    const shouldCheckReferrer = hasInitialHubSpotIndicator || (isInIframe && referrerHasIndicators)

    // Get HubSpot domain
    const hubspotDomain = this.getHubSpotDomain(company, profile, shouldCheckReferrer)

    // Check if referrer matches HubSpot domain
    const referrerHasHubSpot = this.hasHubSpotReferrer(hubspotDomain)

    // Determine if we're in a HubSpot iframe context
    const isHubSpotIframe = isInIframe && (referrerHasHubSpot || isHubSpotExtension || isHubSpotWidget)

    // Determine if any HubSpot conditions are met
    const hasHubSpotConditions = isHubSpotExtension ||
                                 isHubSpotWidget ||
                                 referrerHasHubSpot ||
                                 isHubSpotIframe

    return {
      shouldRedirect: hasHubSpotConditions && isHubSpotEnabled,
      hasHubSpotConditions,
      isHubSpotEnabled,
      hubspotDomain,
      isInIframe,
      isHubSpotIframe,
      referrerHasHubSpot,
      isHubSpotExtension
    }
  }

  /**
   * Determines correct redirect path for HubSpot widget context. Redirects to widget route if in HubSpot iframe, otherwise returns original path
   *
   * @param {string} originalPath Original redirect path from query param or default
   * @param {object} options
   * @param {object} options.route
   * @param {object} options.company
   * @param {object} options.profile
   * @param {boolean} options.isHubSpotWidget
   * @returns {string} Corrected redirect path
   */
  static getCorrectRedirectPath (originalPath, { route = null, company = null, profile = null, isHubSpotWidget = false }) {
    // Preserve the calling and SMS widget routes instead of overriding to the calling widget default below.
    if (this.isWidgetExtensionPath(originalPath) || this.isSmsWidgetPath(originalPath)) {
      return decodeURIComponent(originalPath)
    }

    const context = this.detectHubSpotContext({ route, company, profile, isHubSpotWidget, redirectPath: originalPath })

    if (context.shouldRedirect) {
      return this.WIDGET_ROUTE
    }

    if ((!originalPath || originalPath === '/') && context.isHubSpotEnabled && context.isHubSpotIframe) {
      return this.WIDGET_ROUTE
    }

    return originalPath || '/'
  }

  /**
   * Router guard for HubSpot widget routes
   *
   * @param {object} to
   * @param {object} store
   * @returns {object|null}
   */
  static handleRouterGuard (to, store) {
    const isNavigatingToRootOrInbox = (to.path === '/' || to.path.startsWith('/team-inboxes'))

    if (store.state.isHubSpotWidget && isNavigatingToRootOrInbox) {
      console.log('[HubSpot Routing] Restoring HubSpot widget route')
      return { redirect: this.WIDGET_ROUTE }
    }
    const company = store.state.cache?.currentCompany

    if (isNavigatingToRootOrInbox && !store.state.isHubSpotWidget) {
      const context = this.detectHubSpotContext({
        route: to,
        company,
        isHubSpotWidget: false,
        redirectPath: to.path
      })

      const isHubSpotIframe = context.isHubSpotIframe || (this.isInIframe() && this.hasHubSpotReferrer())
      const shouldRedirect = context.shouldRedirect || (isHubSpotIframe && context.hasHubSpotConditions)

      if (shouldRedirect && isHubSpotIframe) {
        console.log('[HubSpot Routing] Detected HubSpot iframe context, redirecting to widget')
        store.commit('SET_IS_HUBSPOT_WIDGET', true)
        return { redirect: this.WIDGET_ROUTE }
      }
    }

    const isNavigatingToWidget = this.isHubSpotWidgetRoute(to)
    if (isNavigatingToWidget && !store.state.isHubSpotWidget) {
      store.commit('SET_IS_HUBSPOT_WIDGET', true)
    }

    return null
  }

  /**
   * Determines redirect path for login success in HubSpot context
   *
   * @param {string} redirectQuery Redirect query parameter from route
   * @param {object} options Context options (route, company, profile, isHubSpotWidget)
   * @returns {string} Redirect path
   */
  static getLoginRedirectPath (redirectQuery, options) {
    return this.getCorrectRedirectPath(redirectQuery, options)
  }
}

export default HubSpotRoutingService
